import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { StepUpGate, StepUpNotice } from '@/components/admin/step-up/StepUpGate';
import { useRefreshStepUpStatus } from '@/hooks/useStepUpStatus';
import { isStepUpRequired, type StepUpAction } from '@/lib/step-up';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ConfidenceBadge } from '../../components/stories/ConfidenceBadge';
import { TierBadge } from '../../components/stories/TierBadge';
import {
  useAdminStory,
  useEjectArticle,
  useOverrideCanonical,
  useOverrideConfidence,
  useResetOverrides,
  useTriggerSynthesis,
  useResolveTiers,
  useSplitStory,
  useMergeStories,
  useAdminAudit,
  useConfidenceShadow,
} from '../../hooks/admin-stories';
import { ConfidenceShadowPanel } from '../../components/admin/ConfidenceShadowPanel';
import { useStorySources } from '../../hooks/stories';
import { CONFIDENCE_LABELS, type ConfidenceLabel } from '../../types/story';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

type ActionKey = 'eject' | 'split' | 'confidence' | 'merge' | null;

export function StoryAdminDetailPage() {
  const { id = '' } = useParams();
  const [action, setAction] = useState<ActionKey>(null);
  const [selectedArticleIds, setSelectedArticleIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [confidenceChoice, setConfidenceChoice] = useState<ConfidenceLabel>('corroborated');
  const [mergeTargetId, setMergeTargetId] = useState('');

  const { data: story } = useAdminStory(id);
  const { data: sourcesData } = useStorySources(id);
  const { data: auditData } = useAdminAudit({ storyId: id, limit: 20 });
  const shadowQuery = useConfidenceShadow(id);

  const eject = useEjectArticle();
  const split = useSplitStory();
  const merge = useMergeStories();
  const confidence = useOverrideConfidence();
  const canonical = useOverrideCanonical();
  const reset = useResetOverrides();
  const resynthesise = useTriggerSynthesis();
  const resolveTiers = useResolveTiers();
  const refreshStepUp = useRefreshStepUpStatus();

  if (!story) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;

  const sources = sourcesData?.sources ?? [];

  const closeDialog = () => {
    setAction(null);
    setReason('');
    setSelectedArticleIds([]);
  };

  /**
   * KAN-857 (design §5.7 step 2): a destructive submit refused for want of a
   * step-up keeps the dialog open with everything typed, and re-checks the
   * pre-flight so the primary button becomes "Confirm it's you". Nothing is
   * replayed. Any other error surfaces as before.
   */
  const submitGuarded = async (stepUpAction: StepUpAction, run: () => Promise<unknown>) => {
    try {
      await run();
      closeDialog();
    } catch (err) {
      if (isStepUpRequired(err)) {
        await refreshStepUp(stepUpAction);
        return;
      }
      throw err;
    }
  };

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{story.title}</h1>
          <div className="mt-2 flex items-center gap-2">
            <ConfidenceBadge label={story.confidenceLabel} />
            <TierBadge tier={story.publisherBadge.tier} />
            <span className="text-sm text-muted-foreground">{story.publisherBadge.name}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => resynthesise.mutate(id)}>
            Re-synthesise
          </Button>
          <Button variant="secondary" onClick={() => resolveTiers.mutate(id)}>
            Re-run tier resolver
          </Button>
          <Button variant="outline" onClick={() => setAction('confidence')}>
            Override confidence
          </Button>
          <Button variant="outline" onClick={() => setAction('merge')}>
            Merge…
          </Button>
          <Button variant="outline" onClick={() => setAction('split')}>
            Split…
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              reset.mutate({ storyId: id, which: ['canonical', 'title', 'confidence'] })
            }
          >
            Reset overrides
          </Button>
        </div>
      </header>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Members
        </h2>
        <div className="overflow-x-auto rounded border">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Select</th>
                <th className="px-3 py-2 text-left">Publisher</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left">Published</th>
                <th className="px-3 py-2 text-left">Canonical</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.articleId} className="border-t">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedArticleIds.includes(s.articleId)}
                      onChange={(e) => {
                        setSelectedArticleIds((ids) =>
                          e.target.checked
                            ? [...ids, s.articleId]
                            : ids.filter((x) => x !== s.articleId),
                        );
                      }}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      {s.publisher} <TierBadge tier={s.sourceTier} />
                    </span>
                  </td>
                  <td className="px-3 py-2 max-w-md truncate">{s.title}</td>
                  <td className="px-3 py-2 tabular-nums text-muted-foreground">
                    {formatDate(s.publishedAt)}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="radio"
                      name="canonical"
                      checked={s.isCanonical}
                      onChange={() =>
                        canonical.mutate({ storyId: id, articleId: s.articleId })
                      }
                      aria-label={`Promote ${s.title} to canonical`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedArticleIds([s.articleId]);
                        setAction('eject');
                      }}
                    >
                      Eject
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ConfidenceShadowPanel
        shadow={shadowQuery.data}
        isError={shadowQuery.isError}
        titles={Object.fromEntries(sources.map((s) => [s.articleId, s.title]))}
      />

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Synthesis
        </h2>
        <article className="rounded border bg-muted/20 p-4 text-sm">
          {story.synthesisedSummary}
        </article>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Audit log (last 20)
        </h2>
        <ul className="divide-y rounded border">
          {(auditData?.entries ?? []).map((e) => (
            <li
              key={e.id}
              className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
            >
              <span className="font-mono text-muted-foreground">
                {formatDateTime(e.createdAt)}
              </span>
              <span className="font-medium">{e.action}</span>
              <span className="flex-1 truncate text-muted-foreground">{e.reason ?? '—'}</span>
            </li>
          ))}
        </ul>
      </section>

      <Dialog
        open={action === 'eject'}
        onOpenChange={(o) => {
          if (!o) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eject article</DialogTitle>
          </DialogHeader>
          <StepUpNotice action="story.eject" open={action === 'eject'} />
          <Textarea
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <StepUpGate action="story.eject" open={action === 'eject'}>
              <Button
                disabled={!reason.trim() || selectedArticleIds.length !== 1}
                onClick={() =>
                  submitGuarded('story.eject', () =>
                    eject.mutateAsync({
                      storyId: id,
                      articleId: selectedArticleIds[0],
                      reason: reason.trim(),
                    }),
                  )
                }
              >
                Eject
              </Button>
            </StepUpGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={action === 'split'}
        onOpenChange={(o) => {
          if (!o) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Split selected articles into a new story</DialogTitle>
          </DialogHeader>
          <StepUpNotice action="story.split" open={action === 'split'} />
          <p className="text-sm text-muted-foreground">
            Selected: {selectedArticleIds.length} article(s).
          </p>
          <Textarea
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <StepUpGate action="story.split" open={action === 'split'}>
              <Button
                disabled={!reason.trim() || selectedArticleIds.length < 1}
                onClick={() =>
                  submitGuarded('story.split', () =>
                    split.mutateAsync({
                      storyId: id,
                      articleIds: selectedArticleIds,
                      reason: reason.trim(),
                    }),
                  )
                }
              >
                Split
              </Button>
            </StepUpGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={action === 'confidence'}
        onOpenChange={(o) => {
          if (!o) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override confidence label</DialogTitle>
          </DialogHeader>
          <Select
            value={confidenceChoice}
            onValueChange={(v) => setConfidenceChoice(v as ConfidenceLabel)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONFIDENCE_LABELS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c.replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              disabled={!reason.trim()}
              onClick={async () => {
                await confidence.mutateAsync({
                  storyId: id,
                  label: confidenceChoice,
                  reason: reason.trim(),
                });
                closeDialog();
              }}
            >
              Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={action === 'merge'}
        onOpenChange={(o) => {
          if (!o) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge into another story</DialogTitle>
          </DialogHeader>
          <StepUpNotice action="story.merge" open={action === 'merge'} />
          <p className="text-sm text-muted-foreground">
            Paste the destination story ID. Current story will be absorbed (archived).
          </p>
          <Textarea
            placeholder="Destination story ID"
            value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)}
          />
          <Textarea
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <StepUpGate action="story.merge" open={action === 'merge'}>
              <Button
                disabled={!reason.trim() || !mergeTargetId.trim()}
                onClick={() =>
                  submitGuarded('story.merge', () =>
                    merge.mutateAsync({
                      sourceStoryId: id,
                      destinationStoryId: mergeTargetId.trim(),
                      reason: reason.trim(),
                    }),
                  )
                }
              >
                Merge
              </Button>
            </StepUpGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
